import EmblaCarousel from 'embla-carousel';
import {
    ANNUAL,
    EVERY_30_DAYS,
    getFilteredSortedPlans,
    formatFeature,
    translateMe,
    isPlanButtonDisabled,
    getPlanButtonText,
    deepEqual,
    emitEvent, formatString,
    getPlanChangeConfirmationContent,
    isPlanDowngrade,
} from '@/helpers/helper.js';

/**
 * PlanTablePolaris custom element
 * @property {Array} plans - List of plan objects to display
 * @property {Array} features - List of feature objects
 * @property {Object} currentPlan - The current plan object
 * @property {Boolean} hasActiveCharge - Whether the user has an active charge
 * @property {String} shopifyPlan - The current Shopify plan
 * @property {Object} promotionalDiscount - Promotional discount object
 * @property {String} selectedInterval - Selected interval (monthly/annual)
 * @property {Object} featureValues - Feature values object
 */
class PlanTablePolaris extends HTMLElement {
    static get observedAttributes() {
        return [
            'has-active-charge',
            'shopify-plan',
            'selected-interval',
        ];
    }

    constructor() {
        super();
        this.attachShadow({mode: 'open'});

        // external instances
        this._embla = null;
        this._emblaViewport = null;
        this._wheelListener = null;
        this._wheelDeltaAccumulator = 0;

        // data
        this._plans = [];
        this._features = [];
        this._currentPlan = null;
        this._hasActiveCharge = false;
        this._shopifyPlan = '';
        this._selectedInterval = EVERY_30_DAYS;
        this._promotionalDiscount = null;
        this._featureValues = {};

        // UI state
        this._showFeatures = false;
        this._largeCard = false;
        this._initialized = false;
        this._loadingPlanId = null;

        // observers
        this._resizeObserver = null;
        this._featureResizeObserver = null;
        this._headerResizeObserver = null;

        // bindings
        this._handleResize = this._handleResize.bind(this);
    }

    connectedCallback() {
        if (!this._initialized) {
            this._initialized = true;
            this._renderShell();
            this._initCarousel();
        }
        if (!this._resizeObserver) {
            this._resizeObserver = new ResizeObserver(() => {
                this._embla?.reInit();
                this._syncAllHeights();
                this._setDynamicSlideWidths();
            });
            this._resizeObserver.observe(this);
        }
        this._render();

        setTimeout(() => {
            this._syncAllHeights();
            this._setDynamicSlideWidths();
        }, 0);
    }

    disconnectedCallback() {
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }
        if (this._featureResizeObserver) {
            this._featureResizeObserver.disconnect();
            this._featureResizeObserver = null;
        }
        if (this._headerResizeObserver) {
            this._headerResizeObserver.disconnect();
            this._headerResizeObserver = null;
        }
        this._removePlanButtonListeners();
        this._removeToggleLinkListener();
        this._unbindWheelScroll();
        if (this._embla) {
            this._embla.destroy();
            this._embla = null;
        }
    }

    attributeChangedCallback(name, oldValue, newValue) {
        switch (name) {
            case 'has-active-charge':
                this.hasActiveCharge = newValue === 'true';
                break;
            case 'shopify-plan':
                this.shopifyPlan = newValue;
                break;
            case 'selected-interval':
                this.selectedInterval = newValue;
                break;
        }
        this._render();
    }


    // --- Property Getters/Setters ---
    get plans() { return this._plans; }
    set plans(value) {
        if (!deepEqual(this._plans, value)) {
            this._plans = Array.isArray(value) ? value : [];
            this._render();
        }
    }

    get features() { return this._features; }
    set features(value) {
        if (!deepEqual(this._features, value)) {
            this._features = Array.isArray(value) ? value : [];
            this._render();
        }
    }

    get currentPlan() { return this._currentPlan; }
    set currentPlan(value) {
        if (!deepEqual(this._currentPlan, value)) {
            this._currentPlan = value;
            this._render();
        }
    }

    get hasActiveCharge() { return this._hasActiveCharge; }
    set hasActiveCharge(value) {
        if (this._hasActiveCharge !== !!value) {
            this._hasActiveCharge = !!value;
            this._render();
        }
    }

    get shopifyPlan() { return this._shopifyPlan; }
    set shopifyPlan(value) {
        if (this._shopifyPlan !== value) {
            this._shopifyPlan = value || '';
            this._render();
        }
    }

    get promotionalDiscount() { return this._promotionalDiscount; }
    set promotionalDiscount(value) {
        if (!deepEqual(this._promotionalDiscount, value)) {
            this._promotionalDiscount = value || null;
            this._render();
        }
    }

    get featureValues() { return this._featureValues; }
    set featureValues(value) {
        if (!deepEqual(this._featureValues, value)) {
            this._featureValues = value || {};
            this._render();
        }
    }

    get selectedInterval() { return this._selectedInterval; }
    set selectedInterval(value) {
        if (this._selectedInterval !== value) {
            this._selectedInterval = value;
            this._render();
        }
    }

    get showFeatures() { return this._showFeatures; }
    set showFeatures(value) {
        if (this._showFeatures !== !!value) {
            this._showFeatures = !!value;
            this._render();
        }
    }

    get largeCard() { return this._largeCard; }
    set largeCard(value) {
        const parsed = !!value;
        if (this._largeCard !== parsed) {
            this._largeCard = parsed;
            this._render();
        }
    }


    // --- Internal Helpers ---
    _getPlansByInterval(interval) {
        try {
            let filterData = getFilteredSortedPlans(
                Array.isArray(this._plans) ? this._plans : [],
                interval,
                this._promotionalDiscount,
                this._shopifyPlan,
                this._currentPlan,
                this._hasActiveCharge
            );
            return Array.isArray(filterData.filteredPlans) ? filterData.filteredPlans : [];
        } catch (e) {
            console.error('Error in _getPlansByInterval:', e, this._plans);
            return [];
        }
    }

    _getSlidesPerView() {
        const width = window.innerWidth;
        let slidesPerView;
        if (width <= 640) slidesPerView = 1.2;
        else if (width <= 1024) slidesPerView = 2;
        else if (width < 1440) slidesPerView = 3;
        else if (width < 1600) slidesPerView = 4;
        else slidesPerView = 5;
        if (this._largeCard && slidesPerView >= 4) {
            slidesPerView -= 1;
        }
        return Math.min(slidesPerView, this._plans.length || 1);
    }

    _getGroupedFeatures(plans, features) {
        if (!features.length) return [];
        const usedFeatures = new Set();
        plans.forEach(plan => {
            if (plan.features) {
                Object.keys(plan.features).forEach(featureUuid => {
                    usedFeatures.add(featureUuid);
                });
            }
        });
        let groupedFeatures = {};
        let hasGroupFields = false;
        features
            .filter(feature => usedFeatures.has(feature.uuid) && !feature.hidden_feature)
            .forEach(feature => {
                if (feature.group && feature.group_order) hasGroupFields = true;
                const group = hasGroupFields ? (feature.group || 'Default') : 'Default';
                if (!groupedFeatures[group]) {
                    groupedFeatures[group] = {
                        name: group,
                        order: hasGroupFields ? (feature.group_order || 999) : 999,
                        features: []
                    };
                }
                groupedFeatures[group].features.push(feature);
            });
        const groups = Object.values(groupedFeatures).sort((a, b) => a.order - b.order);
        if (groups.length === 1 || !hasGroupFields) groups.forEach(group => group.name = '');
        return groups;
    }

    _hasFeature(plan, feature) {
        return plan.features && !!plan.features[feature.uuid];
    }


    // --- Render Methods ---
    _renderShell() {
        this.shadowRoot.innerHTML = `
            <style>
                :host {
                    display: block;
                }
                .plan-table__container {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                    max-width: calc(1200px - 8px);
                    margin: 32px auto;
                    box-sizing: border-box;
                    position: relative;
                }
                .plan-table__container .plan-table__features-toggle {
                    display: flex;
                    justify-content: center;
                }
                .plan-table__content {
                    border-radius: 16px;
                    box-shadow: 0 4px 6px -1px #0000001a;
                    background-color: #fff;
                    border: 1px solid #e5e5e5;
                }
                .plan-table {
                    border: 1px solid #e5e5e5;
                    display: grid;
                    width: calc(100% + 2px);
                    grid-template-columns: repeat(5, 1fr);
                    grid-gap: 0;
                    box-sizing: border-box;
                    max-height: 500px;
                    overflow-y: auto;
                }
                .plan-table__features {
                    display: flex;
                    flex-direction: column;
                    grid-column: span 2;
                }
                .plan-table__feature-name.without-group,
                .plan-table__feature-value.without-group {
                    display: none;
                }
                .plan-table__feature-name.with-group,
                .plan-table__feature-value.with-group {
                    background: #f5f5f5;
                    font-weight: 600;
                    min-height: 24px;
                    padding: 8px 16px;
                    font-size: 14px;
                }
                .plan-table__feature-value span.plan-table__feature-value-span {
                    font-size: 13px;
                    line-height: 20px;
                }
                .plan-table__plans {
                    grid-column: span 3;
                    position: relative;
                    box-sizing: border-box;
                }
                .embla {
                    overflow: hidden;
                    width: 100%;
                }
                .embla__container {
                    display: flex;
                    gap: 0;
                    align-items: stretch;
                }
                .embla__slide {
                    min-width: 0;
                    display: flex;
                    justify-content: center;
                }
                .plan-table__controls {
                    display: flex;
                    justify-content: space-between;
                    gap: 12px;
                    margin-top: 16px;
                    width:calc(100% - 64px);
                    position: absolute;
                    left: 50%;
                    top: 50%;
                    transform: translate(-50%, -50%);
                }
                .plan-table__arrow-group { 
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    position: relative;
                }
                .plan-table__arrow-count {
                    position: absolute;
                    top: -44px;
                    background-color: #fff;
                    color: #333;
                    font-weight: 500;
                    padding: 6px 12px;
                    border-radius: 8px;
                    font-size: 14px;
                    line-height: 20px;
                    white-space: nowrap;
                    box-shadow: 0 4px 8px #00000026;
                }
                .plan-table__arrow-count::after {
                    content: '';
                    position: absolute;
                    left: 50%;
                    top: 100%;
                    transform: translateX(-50%);
                    width: 0;
                    height: 0;
                    border-left: 8px solid transparent;
                    border-right: 8px solid transparent;
                    border-top: 8px solid #fff;
                    filter: drop-shadow(0 1px 2px #0001);
                }
                .plan-header-wrapper {
                    box-sizing: border-box;
                }
                .plan-table__arrow-count::before {
                    content: '';
                    position: absolute;
                    left: 50%;
                    top: 100%;
                    transform: translateX(-50%);
                    width: 0;
                    height: 0;
                    border-left: 9px solid transparent;
                    border-right: 9px solid transparent;
                    border-top: 9px solid #E3E3E3;
                    z-index: -1;
                }
                .plan-table__plan {
                    width: 100%;
                }
                .plan-table__plan-header {
                    font-weight: bold;
                    padding: 12px 4px;
                    border-bottom: 1px solid #E0E0E0;
                    border-left: 1px solid #E0E0E0;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: space-between;
                    background: #f1f1f1;
                    gap: 12px;
                    box-sizing: border-box;
                }
                .plan-table__plan-header .plan-table__header-content {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 4px;
                }
                .plan-table__feature-header {
                    font-weight: bold;
                    padding: 16px;
                    border-bottom: 1px solid #E0E0E0;
                    background: #f1f1f1;
                    box-sizing: border-box;
                    display: flex;
                    align-items: center;
                }
                .plan-table__feature-name {
                    padding: 12px 16px;
                    min-height: 52px;
                    display: flex;
                    box-sizing: border-box;
                    align-items: center;
                    border-bottom: 1px solid #E0E0E0;
                }
                .plan-table__feature-value {
                    padding: 12px 16px;
                    text-align: center;
                    min-height: 52px;
                    display: flex;
                    box-sizing: border-box;
                    align-items: center;
                    justify-content: center;
                    border-bottom: 1px solid #E0E0E0;
                    border-left: 1px solid #E0E0E0;
                }
                .plan-table__feature-name:last-child,
                .plan-table__feature-value:last-child {
                    border-bottom: none;
                }
                .plan-table__checkmark {
                    display: inline-block;
                    width: 21px;
                    height: 20px;
                }
                .plan-table__main-price {
                    display: flex;
                    flex-direction: row;
                    align-items: baseline;
                    justify-content: center;
                    gap: 8px;
                }
                .plan-table__content h5,
                .plan-table__content h4 {
                    margin: 0;
                    display: flex;
                    align-items: baseline;
                    justify-content: center;
                }
                .plan-table__plan-name {
                    text-align: center;
                    font-size: 16px;
                    line-height: 20px;
                    font-weight: 700;
                }

                .plan-table__plan-price {
                    text-align: center;
                    font-size: 16px;
                    line-height: 20px;
                    font-weight: 700;
                }

                .plan-table__plan-interval {
                    color: #616161;
                    font-weight: 400;
                }

                .plan-table__plan-header .plan-table__strike-price .plan-table__plan-price {
                    text-decoration: line-through;
                    font-size: 13px;
                    line-height: 18px;
                    font-weight: 400;
                }
                .app-manager__width-container,
                .app-manager__width-container *,
                .app-manager__width-container *::before,
                .app-manager__width-container *::after {
                    box-sizing: border-box;
                }
                .app-manager__width-container {
                    padding-left: 96px;
                    padding-right: 96px;
                    max-width: calc(1200px + 8px);
                }
                @media screen and (max-width: 768px) {
                    .app-manager__width-container {
                        max-width: 100%;
                        padding-left: 0;
                        padding-right: 0;
                    }
                    .plan-table__controls {
                        display: none;
                    }
                    .embla__slide {
                        flex: 0 0 50%;
                    }
                }
                @media (max-width: 540px) {
                    .embla__slide {
                        flex: 0 0 75% !important;
                    }
                    .plan-table__features {
                        min-width: 180px;
                    }
                    .plan-table__plan {
                        width: 100%;
                    }
                }
                @media (max-width: 440px) {
                    .embla__slide {
                        flex: 0 0 90% !important;
                    }
                }
            </style>
            <div class="plan-table__container app-manager__width-container">
                <div class="plan-table__content"></div>
            </div>
        `;
        this._container = this.shadowRoot.querySelector('.plan-table__container');
    }

    // --- Event Listener Management ---
    _planButtonListeners = [];
    _toggleLinkListener = null;

    _removePlanButtonListeners() {
        this._planButtonListeners.forEach(({button, handler}) => {
            button.removeEventListener('click', handler);
        });
        this._planButtonListeners = [];
    }

    _removeToggleLinkListener() {
        if (this._toggleLinkListener && this._toggleLinkEl) {
            this._toggleLinkEl.removeEventListener('click', this._toggleLinkListener);
        }
        this._toggleLinkListener = null;
        this._toggleLinkEl = null;
    }

    _render() {
        try {
            this._removePlanButtonListeners();
            this._removeToggleLinkListener();
            const interval = this._selectedInterval === ANNUAL ? ANNUAL : EVERY_30_DAYS;
            const plans = this._getPlansByInterval(interval);
            const featureGroups = this._getGroupedFeatures(plans, Array.isArray(this._features) ? this._features : []);
            const planTableContentDisplay = this._showFeatures ? 'block' : 'none';

            if (!Array.isArray(this._plans) || this._plans.length === 0) {
                this._container.innerHTML = `<div class="empty-state">${translateMe('No plans available')}</div>`;
                return;
            }

            const container = this.shadowRoot.querySelector('.plan-table__container');
            if (!container) {
                return;
            }

            container.innerHTML = `
                <div class="plan-table__features-toggle">
                    <s-paragraph>
                        <s-link>${this._showFeatures ? translateMe('Hide all features') : translateMe('Show all features')}</s-link>
                    </s-paragraph>
                </div>
                <div class="plan-table__content" style="display: ${planTableContentDisplay};">
                    <s-section>
                        <div class="plan-table">
                            <div class="plan-table__features">
                                <div class="plan-table__feature-header">
                                    ${plans.length} ${translateMe('plans available')}
                                </div>
                                ${this._renderFeatureNames(featureGroups)}
                            </div>
                            <div class="plan-table__plans">
                                <div class="embla">
                                    <div class="embla__container">
                                        ${plans.map(plan => this._renderPlanSlide(plan, featureGroups)).join('')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </s-section>
                </div>
                <div class="plan-table__controls">
                    <span class="plan-table__arrow-group">
                        <span id="plan-table__left-tooltip" class="plan-table__arrow-count"></span>
                        <s-button class="plan-table__arrow plan-table__arrow--prev" variant="primary" icon="arrow-left" aria-label="Previous plan" accessibilityLabel="Previous plan"></s-button>
                    </span>
                    <span class="plan-table__arrow-group">
                        <span id="plan-table__right-tooltip" class="plan-table__arrow-count"></span>
                        <s-button class="plan-table__arrow plan-table__arrow--next" variant="primary" icon="arrow-right" aria-label="Next plan" accessibilityLabel="Next plan"></s-button>
                    </span>
                </div>
            `;
            // Always initialize carousel after DOM update
            this._initCarousel();
            // Set dynamic slide widths after rendering
            this._setDynamicSlideWidths();
            // Synchronize feature heights after rendering, using requestAnimationFrame to ensure DOM is painted
            requestAnimationFrame(() => this._syncAllHeights());

            // After setting innerHTML in _render(), add:
            const toggleLink = this.shadowRoot.querySelector('.plan-table__features-toggle s-link');
            if (toggleLink) {
                const handler = () => {
                    this._showFeatures = !this._showFeatures;
                    this._render();
                };
                toggleLink.addEventListener('click', handler);
                this._toggleLinkListener = handler;
                this._toggleLinkEl = toggleLink;
            }
            // Attach click listeners to plan selection buttons after rendering
            const planButtons = this.shadowRoot.querySelectorAll('.plan-table__plan-button s-button');
            planButtons.forEach((btn, idx) => {
                const handler = () => {
                    const interval = this._selectedInterval === ANNUAL ? ANNUAL : EVERY_30_DAYS;
                    const plans = this._getPlansByInterval(interval);
                    const plan = plans[idx];
                    if (this._hasPlanChangeConfirmationContent(plan)) {
                        const content = getPlanChangeConfirmationContent(plan);
                        emitEvent(this, 'app-manager:plan-change-confirmation-request', {
                            source: 'plan-table-polaris',
                            plan,
                            title: content.title,
                            description: content.description,
                            descriptionLines: content.descriptionLines,
                        });
                        return;
                    }
                    this._executePlanChange(plan);
                };
                btn.addEventListener('click', handler);
                this._planButtonListeners.push({button: btn, handler});
            });
        } catch (e) {
            console.error('Error in PlanTablePolaris._render:', e);
        }
    }

    _renderFeatureValue(plan, feature) {
        return this._hasFeature(plan, feature)
            ? (
                feature.value_type === 'boolean'
                    ? this._renderCheckmarkIcon()
                    : `<span>${translateMe(formatFeature(plan.features[feature.uuid]))}</span>`
            )
            : this._renderCrossIcon()
    }

    _renderFeatureNames(featureGroups) {
        const featureGroupsCount = featureGroups.length || 0;
        return featureGroups.map(group => `
            <div class="plan-table__feature-name ${group.name && featureGroupsCount > 1 ? 'with-group' : 'without-group'}" data-feature-id="${formatString(group.name)}">
                ${translateMe(group.name)}
            </div>
            ${group.features.map(feature => `
                <div class="plan-table__feature-name" data-feature-id="${feature.uuid}">${translateMe(feature.name)}</div>
            `).join('')}
        `).join('')
    }

    _renderPlanSlide(plan, featureGroups) {
        let price = Number(plan.price);
        let strikePrice = Number(plan.strike_price);
        if (isNaN(price)) {
            console.error('Invalid plan.price for plan', plan);
            price = 0;
        }
        if (plan.has_discount && isNaN(strikePrice)) {
            console.error('Invalid plan.strike_price for plan', plan);
            strikePrice = null;
        }
        const buttonDisabled = isPlanButtonDisabled(this.shopifyPlan, plan, this.hasActiveCharge, this.currentPlan);
        const buttonContent = getPlanButtonText(this._shopifyPlan, plan, this.currentPlan, true, this.hasActiveCharge);
        const isThisPlanLoading = this._loadingPlanId === plan.id;
        return `
            <div class="embla__slide">
                <div class="plan-table__plan">
                    <div class="plan-table__plan-header">
                        <div class="plan-table__header-content">
                            <h4 class="plan-table__plan-name">${plan.name}</h4>
                            <div class="plan-table__plan-prices">
                                ${plan.has_discount 
                                    ? `<div class="plan-table__strike-price">
                                        <h5>
                                            <span class="plan-table__plan-price">
                                                ${strikePrice !== null ? strikePrice.toFixed(2) : ''}
                                            </span>
                                            ${plan.has_discount
                                                ? `<span class="plan-table__plan-interval">/${translateMe(this._selectedInterval === ANNUAL ? "yr" : "mo")}</span>`
                                                : ``
                                            }
                                        </h5>
                                    </div>` 
                                    : ``
                                }
                                <div class="plan-table__main-price">
                                    ${parseFloat(plan.price) > 0
                                        ? `<h4>
                                            <span class="plan-table__plan-price">$${price.toFixed(2)}</span>
                                            <span class="plan-table__plan-interval">/${translateMe(this._selectedInterval === ANNUAL ? "yr" : "mo")}</span>
                                        </h4>`
                                        : `<h4><span class="plan-table__plan-price">${translateMe("Free")}</span></h4>`}
                                </div>
                            </div>
                        </div>
                        <div class="plan-table__plan-button">
                            <s-button variant="secondary" aria-label="Select ${plan.name} plan" ${buttonDisabled || this._loadingPlanId ? 'disabled' : ''} ${isThisPlanLoading ? 'loading' : ''}>${buttonContent}</s-button>
                        </div>
                    </div>
                    ${featureGroups.map(group => `
                        <div class="plan-table__feature-value ${group.name ? 'with-group' : 'without-group'}" data-feature-id="${formatString(group.name)}">
                            <span class="plan-table__feature-value-span" style="visibility: hidden;">
                                1
                            </span>
                        </div>
                        ${group.features.map(feature => `
                            <div class="plan-table__feature-value" data-feature-id="${feature.uuid}">
                                ${this._renderFeatureValue(plan, feature)}
                            </div>
                        `).join('')}
                    `).join('')}
                </div>
            </div>
        `;
    }

    _renderCheckmarkIcon() {
        return `<span class="plan-table__checkmark">
            <svg width="21" height="20" viewBox="0 0 21 20" fill="white" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_859_9134)">
                    <path d="M3.25 10C3.25 10.9849 3.44399 11.9602 3.8209 12.8701C4.19781 13.7801 4.75026 14.6069 5.4467 15.3033C6.14314 15.9997 6.96993 16.5522 7.87987 16.9291C8.78982 17.306 9.76509 17.5 10.75 17.5C11.7349 17.5 12.7102 17.306 13.6201 16.9291C14.5301 16.5522 15.3569 15.9997 16.0533 15.3033C16.7497 14.6069 17.3022 13.7801 17.6791 12.8701C18.056 11.9602 18.25 10.9849 18.25 10C18.25 9.01509 18.056 8.03982 17.6791 7.12987C17.3022 6.21993 16.7497 5.39314 16.0533 4.6967C15.3569 4.00026 14.5301 3.44781 13.6201 3.0709C12.7102 2.69399 11.7349 2.5 10.75 2.5C9.76509 2.5 8.78982 2.69399 7.87987 3.0709C6.96993 3.44781 6.14314 4.00026 5.4467 4.6967C4.75026 5.39314 4.19781 6.21993 3.8209 7.12987C3.44399 8.03982 3.25 9.01509 3.25 10Z" stroke="#1A1A1A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M8.25 9.99998L9.91667 11.6666L13.25 8.33331" stroke="#1A1A1A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                <clipPath id="clip0_859_9134">
                    <rect width="20" height="20" fill="white" transform="translate(0.75)"/>
                </clipPath>
                </defs>
            </svg>
        </span>`
    }

    _renderCrossIcon() {
        return `<span class="plan-table__checkmark">
            <svg width="21" height="20" viewBox="0 0 21 20" fill="white" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_859_9139)">
                    <path d="M15.75 5L5.75 15" stroke="#5C5C5C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M5.75 5L15.75 15" stroke="#5C5C5C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                    <clipPath id="clip0_859_9139">
                        <rect width="20" height="20" fill="white" transform="translate(0.75)"/>
                    </clipPath>
                </defs>
            </svg>
        </span>`
    }

    _updateArrowVisibilityAndTooltips() {
        if (!this._embla) return;
        const prevBtn = this.shadowRoot.querySelector('.plan-table__arrow--prev');
        const nextBtn = this.shadowRoot.querySelector('.plan-table__arrow--next');
        const prevTooltip = this.shadowRoot.querySelector('#plan-table__left-tooltip');
        const nextTooltip = this.shadowRoot.querySelector('#plan-table__right-tooltip');

        const slidesPerView = this._getSlidesPerView();
        const total = this._plans.length;
        const selected = this._embla.selectedScrollSnap();
        const canScrollPrev = this._embla.canScrollPrev();
        const canScrollNext = this._embla.canScrollNext();
        const leftCount = selected;
        const rightCount = total - (selected + slidesPerView);
        prevBtn.style.display = canScrollPrev ? '' : 'none';
        nextBtn.style.display = canScrollNext ? '' : 'none';
        if (prevTooltip) {
            prevTooltip.textContent = leftCount > 0 ? `+${leftCount} ${translateMe(leftCount === 1 ? 'plan' : 'plans')}` : '';
            prevTooltip.style.display = canScrollPrev && leftCount > 0 ? '' : 'none';
        }
        if (nextTooltip) {
            nextTooltip.textContent = rightCount > 0 ? `+${rightCount} ${translateMe(rightCount === 1 ? 'plan' : 'plans')}` : '';
            nextTooltip.style.display = canScrollNext && rightCount > 0 ? '' : 'none';
        }
    }


    // --- DOM Update Helpers ---
    _handleResize() {
        this._render();
    }

    _setDynamicSlideWidths() {
        const slides = this.shadowRoot.querySelectorAll('.embla__slide');
        const plansCount = this._plans.length;
        const slidesPerView = this._getSlidesPerView();
        if (plansCount === 0) return;

        const effectiveSlides = Math.min(slidesPerView, plansCount);
        slides.forEach(slide => {
            slide.style.flex = `0 0 ${100 / effectiveSlides}%`;
        });

        const container = this.shadowRoot.querySelector('.plan-table__container');
        const maxWidths = { 2: 808, 3: 1208, 4: 1448, 5: 1608 };
        const key = Math.floor(effectiveSlides);
        container.style.maxWidth = maxWidths[key] ? `${maxWidths[key]}px` : '';
    }

    _syncHeaderHeights() {
        const planHeaders = this.shadowRoot.querySelectorAll('.plan-table__plan-header');
        const featureHeader = this.shadowRoot.querySelector('.plan-table__feature-header');
        // Reset heights
        planHeaders.forEach(header => header.style.height = '');
        if (featureHeader) featureHeader.style.height = '';
        // Measure tallest
        let maxHeaderHeight = 0;
        planHeaders.forEach(header => {
            const h = header.getBoundingClientRect().height || header.offsetHeight;
            if (h > maxHeaderHeight) maxHeaderHeight = h;
        });
        if (featureHeader) {
            const h = featureHeader.getBoundingClientRect().height || featureHeader.offsetHeight;
            if (h > maxHeaderHeight) maxHeaderHeight = h;
        }
        // Apply the tallest height
        planHeaders.forEach(header => header.style.height = `${maxHeaderHeight}px`);
        if (featureHeader) featureHeader.style.height = `${maxHeaderHeight}px`;
    }

    _syncFeatureValueHeights() {
        // Get all unique feature ids
        const featureNameNodes = Array.from(this.shadowRoot.querySelectorAll('.plan-table__feature-name[data-feature-id]'));
        const featureValueNodes = Array.from(this.shadowRoot.querySelectorAll('.plan-table__feature-value[data-feature-id]'));
        const featureIds = new Set([
            ...featureNameNodes.map(n => n.getAttribute('data-feature-id')),
            ...featureValueNodes.map(n => n.getAttribute('data-feature-id')),
        ]);
        featureIds.forEach(featureId => {
            // Get all nodes for this feature id
            const names = Array.from(this.shadowRoot.querySelectorAll(`.plan-table__feature-name[data-feature-id="${featureId}"]`));
            const values = Array.from(this.shadowRoot.querySelectorAll(`.plan-table__feature-value[data-feature-id="${featureId}"]`));
            // Reset heights
            [...names, ...values].forEach(node => node.style.height = '');
            // Find max height
            let maxHeight = 0;
            [...names, ...values].forEach(node => {
                const h = node.getBoundingClientRect().height || node.offsetHeight;
                if (h > maxHeight) maxHeight = h;
            });
            // Set all to max height
            [...names, ...values].forEach(node => node.style.height = `${maxHeight}px`);
        });
    }

    _observeFeatureRows() {
        // Disconnect previous observer if any
        if (this._featureResizeObserver) {
            this._featureResizeObserver.disconnect();
        }
        // Observe all feature name/value elements for size changes
        this._featureResizeObserver = new ResizeObserver(() => {
            this._syncFeatureValueHeights();
        });
        const nodes = this.shadowRoot.querySelectorAll('.plan-table__feature-name[data-feature-id], .plan-table__feature-value[data-feature-id]');
        nodes.forEach(node => this._featureResizeObserver.observe(node));
    }

    _observeHeaderRows() {
        if (this._headerResizeObserver) {
            this._headerResizeObserver.disconnect();
        }
        this._headerResizeObserver = new ResizeObserver(() => {
            this._syncHeaderHeights();
        });
        const nodes = this.shadowRoot.querySelectorAll('.plan-table__plan-header, .plan-table__feature-header');
        nodes.forEach(node => this._headerResizeObserver.observe(node));
    }

    _syncAllHeights() {
        this._syncHeaderHeights();
        this._syncFeatureValueHeights();
        this._observeFeatureRows();
        this._observeHeaderRows();
    }

    _hasPlanChangeConfirmationContent(plan) {
        const content = getPlanChangeConfirmationContent(plan);
        if (!(content?.title || (Array.isArray(content?.descriptionLines) && content.descriptionLines.length) || content?.description)) return false;
        return isPlanDowngrade(plan, this._currentPlan, this._hasActiveCharge);
    }

    _executePlanChange(plan) {
        if (!plan || !plan.id || this._loadingPlanId) return;
        this._loadingPlanId = plan.id;
        this._render();
        emitEvent(this, 'app-manager:plan-click', { plan });
        setTimeout(() => {
            this._loadingPlanId = null;
            this._render();
        }, 2000);
    }

    _bindWheelScroll(viewport) {
        this._unbindWheelScroll();
        if (!viewport) return;

        this._emblaViewport = viewport;
        this._wheelDeltaAccumulator = 0;
        this._wheelListener = (event) => {
            if (!this._embla) return;

            const horizontalDelta = event.deltaX;
            const verticalDelta = event.deltaY;
            const horizontalAbs = Math.abs(horizontalDelta);
            const verticalAbs = Math.abs(verticalDelta);
            const horizontalIntentRatio = 1.2;
            const hasHorizontalIntent = horizontalAbs > 0 && horizontalAbs >= verticalAbs * horizontalIntentRatio;
            const useShiftWheelAsHorizontal = event.shiftKey && verticalAbs > 0;

            if (!hasHorizontalIntent && !useShiftWheelAsHorizontal) {
                this._wheelDeltaAccumulator = 0;
                return;
            }

            const effectiveDelta = hasHorizontalIntent ? horizontalDelta : verticalDelta;
            this._wheelDeltaAccumulator += effectiveDelta;
            if (Math.abs(this._wheelDeltaAccumulator) < 36) return;

            const direction = this._wheelDeltaAccumulator > 0 ? 1 : -1;
            this._wheelDeltaAccumulator = 0;

            if (direction > 0 && this._embla.canScrollNext()) {
                event.preventDefault();
                this._embla.scrollNext();
            } else if (direction < 0 && this._embla.canScrollPrev()) {
                event.preventDefault();
                this._embla.scrollPrev();
            }
        };

        this._emblaViewport.addEventListener('wheel', this._wheelListener, { passive: false });
    }

    _unbindWheelScroll() {
        if (this._emblaViewport && this._wheelListener) {
            this._emblaViewport.removeEventListener('wheel', this._wheelListener);
        }
        this._emblaViewport = null;
        this._wheelListener = null;
        this._wheelDeltaAccumulator = 0;
    }


    // --- External Library/Integration ---
    _initCarousel() {
        const viewport = this.shadowRoot.querySelector('.embla');
        if (!viewport || !this._plans.length) {
            this._unbindWheelScroll();
            if (this._embla) {
                this._embla.destroy();
                this._embla = null;
            }
            return;
        }
        if (this._embla) this._embla.destroy();
        this._embla = EmblaCarousel(viewport, {
            align: 'start',
            loop: false,
            dragFree: false,
        });
        this._bindWheelScroll(viewport);
        this._initControls();
        this._embla.on('select', () => this._updateArrowVisibilityAndTooltips());
        this._embla.on('reInit', () => this._updateArrowVisibilityAndTooltips());
        this._updateArrowVisibilityAndTooltips();
    }

    _initControls() {
        const prev = this.shadowRoot.querySelector('.plan-table__arrow--prev');
        const next = this.shadowRoot.querySelector('.plan-table__arrow--next');
        if (!prev || !next) return;
        if (this._onPrevClick && prev.removeEventListener) prev.removeEventListener('click', this._onPrevClick);
        if (this._onNextClick && next.removeEventListener) next.removeEventListener('click', this._onNextClick);
        this._onPrevClick = () => this._embla && this._embla.scrollPrev();
        this._onNextClick = () => this._embla && this._embla.scrollNext();
        prev.addEventListener('click', this._onPrevClick);
        next.addEventListener('click', this._onNextClick);
        if (this._embla && this._embla.on) {
            if (this._onEmblaReInit) this._embla.off && this._embla.off('reInit', this._onEmblaReInit);
            this._onEmblaReInit = () => {
                // this._createDots();
            };
            this._embla.on('reInit', this._onEmblaReInit);
        }
    }
}

if (!customElements.get('plan-table-polaris')) {
    customElements.define('plan-table-polaris', PlanTablePolaris);
}

export {PlanTablePolaris};